import { useEffect, useState } from 'react';
import { api } from '../api';
import type { GuardrailOut, KeyGuardrailChangeIn, KeyOut } from '../types';

interface GuardrailChangeFormProps {
  keyItem: KeyOut;
  workspaceId: string;
  /** The key's guardrail now, preselected; null when it has none or it is unknown. */
  currentGuardrailId: string | null;
  onSubmit: (data: KeyGuardrailChangeIn) => Promise<void>;
  onCancel: () => void;
  submitting: boolean;
}

/**
 * A platform admin's direct guardrail change: permanent, applied at once, no approval.
 * The temporary, approval-based route stays under "Request guardrail".
 */
export function GuardrailChangeForm({
  keyItem,
  workspaceId,
  currentGuardrailId,
  onSubmit,
  onCancel,
  submitting,
}: GuardrailChangeFormProps) {
  const [guardrails, setGuardrails] = useState<GuardrailOut[] | null>(null);
  const [selected, setSelected] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    api
      .guardrails(workspaceId)
      .then((res) => {
        if (!live) return;
        setGuardrails(res.items);
        const current = res.items.find((g) => g.id === currentGuardrailId);
        setSelected(current?.id ?? res.items[0]?.id ?? '');
      })
      .catch((err: unknown) => {
        if (!live) return;
        setGuardrails([]);
        setError(err instanceof Error ? err.message : String(err));
      });
    return () => {
      live = false;
    };
  }, [workspaceId, currentGuardrailId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!selected) {
      setError('Please select a guardrail');
      return;
    }
    try {
      await onSubmit({ guardrail_id: selected, reason: reason.trim() || null });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  if (guardrails === null) {
    return (
      <div className="create-key-form">
        <h3>Change guardrail</h3>
        <p>Loading guardrails...</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="create-key-form">
      <h3>Change guardrail</h3>
      <p className="muted small">
        Applied to OpenRouter immediately and permanently, replacing any current or temporary
        assignment. Use &ldquo;Request guardrail&rdquo; for a temporary change instead.
      </p>

      <div className="form-group">
        <label htmlFor="change-guardrail-key">Key</label>
        <div id="change-guardrail-key" className="mono readonly">
          {keyItem.name}
        </div>
      </div>

      {guardrails.length === 0 ? (
        <div className="callout callout-warning">
          No guardrails are defined in this key&apos;s workspace.
        </div>
      ) : (
        <div className="form-group">
          <label htmlFor="change-guardrail">Guardrail</label>
          <select
            id="change-guardrail"
            value={selected}
            onChange={(e) => setSelected(e.target.value)}
            disabled={submitting}
          >
            {guardrails.map((g) => (
              <option key={g.id} value={g.id}>
                {g.name ?? g.id}
                {g.id === currentGuardrailId ? ' (current)' : ''}
              </option>
            ))}
          </select>
        </div>
      )}

      <div className="form-group">
        <label htmlFor="change-guardrail-reason">Note for the audit log (optional)</label>
        <textarea
          id="change-guardrail-reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          disabled={submitting}
          rows={2}
          style={{ width: '100%', resize: 'vertical' }}
        />
      </div>

      {error ? (
        <div className="form-error" role="alert">
          {error}
        </div>
      ) : null}

      <div className="form-actions">
        <button type="button" onClick={onCancel} disabled={submitting}>
          Cancel
        </button>
        <button
          type="submit"
          className="primary"
          disabled={submitting || !selected || selected === currentGuardrailId}
        >
          {submitting ? 'Applying...' : 'Change guardrail'}
        </button>
      </div>
    </form>
  );
}
